import React, {useMemo, useState} from 'react';
import {Empty, Input, Modal, Select} from "antd";
import {useTalentTeam} from "../../../providers/TalentTeamProvider";
import {useAuth} from "../../../providers/AuthProvider";
import {useDebounce} from "react-use";
import UserImageCircle from "../../../components/UserImageCircle";
import LoadingSpinner from "../../../components/LoadingSpinner";
import {useAPI} from "../../../config/API";

const { Search } = Input;

const NotFoundContent = ({search, isLoading, onInvite}) => {

    const {
        team,
    } = useTalentTeam();

    const existingMemberFound = useMemo(
        () => {
            let positionEmails = TB.map(team.positions, 'user.email');

            let inviteEmails = TB.map([...(team.pending_invites || []), ...(team.rejected_invites || [])], 'recipient.email');
            inviteEmails = TB.filter(inviteEmails, email => !!email);

            return [...inviteEmails, ...positionEmails].includes(search)
        },
        [team?.positions,team.pending_invites, search],
    );


    const isValidEmail = (email) => {
        return String(email)
            .toLowerCase()
            .match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
    };
    const EmptyDescription = useMemo(() => {
        if (search && isValidEmail(search)) {
            if (existingMemberFound) {
                return "Member Already Invited"
            }
            return  (
                <p className="px-10 mx-auto whitespace-pre text-center">
                    {"Account not found for\n"}<span className="text-info text-underline">{search}</span>.
                </p>
            )
        }
        return "No users found"
    }, [search, existingMemberFound]);

    if(search?.length < 3) return null;

    if(!existingMemberFound && isLoading) {
        return (
            <div className="flex flex-col items-center justify-center p-3">
                <LoadingSpinner size="sm"/>
            </div>
        )
    }

    return (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={EmptyDescription}>
            {search && isValidEmail(search) && (
                <div className="flex flex-col items-center gap-2 mt-2">
                    <button className="btn btn-sm btn-dark" disabled={existingMemberFound} onClick={onInvite}>
                        <p className="text-xs text-white">
                            Invite Member
                        </p>
                    </button>
                </div>
            )}
        </Empty>
    )
}

function InviteTeamMemberModal(props) {
    const {auth} = useAuth();
    const [modelOpen, setModalOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState(undefined);
    const [debounceLoading, setDebounceLoading] = useState(false);
    const [debounceInput, setDebounceInput] = useState('');
    const [existingMember, setExistingMember] = useState(undefined);
    // Kept out of `existingMember` on purpose: spreading that into a plain object
    // dropped the User model's prototype, so `existingMember.fullName()` blew up
    // on the next render.
    const [title, setTitle] = useState('');

    const [isSearchReady, cancel] = useDebounce(
        () => {
            setDebounceInput(search);
        },
        400, [search]
    );
    const {
        team,
        isLoading,
        inviteTalentMember,
        inviteExternalMember,
        isUpdating
    } = useTalentTeam();

    const {
        data,
        isFetching,
        error,
        refetch
    } = useAPI(['searchAllTalentForMemberInvite', {team, search: debounceInput}], {
        enabled: modelOpen && search?.length > 0 && debounceInput?.length >= 3 && !isUpdating && existingMember === undefined,
    })


    const [isLoadingReady, cancelLoading] = useDebounce(
        () => {
            setDebounceLoading(isFetching);
        },
        400, [isFetching]
    );


    const searchOptions = useMemo(() => {
        if (!search?.length) return [];

        return TB.map(data || [], option => ({
            value: option.id,
            display: option.fullName?.(),
            label: (
                <div className="flex flex-row items-center justify-between">
                    <div className="flex flex-row items-center gap-2">
                        <UserImageCircle user={option} size={40}/>
                        <p className="text-sm">{option.fullName?.()}</p>
                    </div>
                    {option.location?.city && (
                        <p className="text-xxs text-info text-right whitespace-nowrap">
                            {option.location?.city}, {option.location.state}
                        </p>
                    )}
                </div>
            ),
        }));
    }, [search,data, isFetching]);


    const toggleSearchModal = async() => {
        await setModalOpen(!modelOpen)
    }

    const onInviteExternal = () => {
        setSearchInput(search);
        return setExistingMember(false)
    }

    const onMemberSelected = (newValue) => {
        let selected = TB.find(data || [], ['id', newValue])
        if (selected) {
            setSearchInput(selected.fullName?.())
            // Seed the position title from the talent's own job title.
            setTitle(TB.truncate(selected.job_title || '', {length: 100, omission: ''}))
            return setExistingMember(selected)
        }
    }

    const onConfirmInvite = async() => {
        let result;
        if (existingMember) {
            result = await inviteTalentMember(existingMember, title)
        }else {
            result = await inviteExternalMember(searchInput)
        }
        if (result) return toggleSearchModal();
    }
    return (
        <React.Fragment>
            <button className="btn btn-primary" onClick={toggleSearchModal} disabled={!team.can_manage || isUpdating || isLoading}>
                <i className="fas fa-plus mr-2 text-white" /> Invite Team Member
            </button>
            <Modal
                destroyOnClose={true}
                open={modelOpen}
                afterClose={async () => {
                    await TB.queryClient.invalidateQueries(['searchAllTalentForMemberInvite', {team, search: debounceInput}])
                    setSearchInput(undefined)
                    setSearch("")
                    setExistingMember(undefined)
                    setTitle('')

                }}
                closable={false}
                title={null}
                footer={null}
            >
                <div className="talent-team-member-invite">

                    <div className="self-stretch flex flex-col items-stretch gap-4">
                        <div className={TB.mapClass("h-[16px] mt-[-24px] mx-[-24px]", {
                            "bg-danger": existingMember === false || existingMember?.is_active === false,
                            "bg-gray": existingMember === undefined,
                            "bg-success": existingMember && existingMember?.is_active
                        })}></div>
                        <p className="text-2xl font-medium">
                            {existingMember === undefined && "Invite Team Member"}
                            {existingMember === false && "Invited Team Member Not on Wripple"}
                            {existingMember && existingMember.is_active && "Team Member Active"}
                            {existingMember && !existingMember.is_active && "Invited Team Member Not Active"}
                        </p>
                    </div>

                    <hr className="my-4 mx-[-24px]"/>

                    {existingMember === undefined && (
                        <React.Fragment>
                            <p className="mb-4">
                                Search for potential team members on Wripple by typing their name or email. Or if your desired team member isn't on Wripple, just enter their email below we will invite them to join Wripple and your team.
                            </p>
                            <Select
                                style={{width: '100%'}}
                                name="search_users"
                                placeholder="Search by Name or Email"
                                maxLength={255}
                                showSearch
                                options={searchOptions}
                                optionLabelProp="display"
                                defaultActiveFirstOption={false}
                                filterOption={false}
                                disabled={isUpdating}
                                value={searchInput}
                                onSearch={setSearch}
                                onChange={onMemberSelected}
                                notFoundContent={(
                                    <NotFoundContent
                                        search={search}
                                        onInvite={onInviteExternal}
                                        isLoading={search !== debounceInput || isFetching || debounceLoading}/>
                                )}
                                loading={isFetching || debounceLoading}
                            />


                        </React.Fragment>
                    )}

                    {existingMember && (existingMember.is_active ? (
                        (
                            <div className="flex flex-col gap-3">
                                <p>
                                    We have found <span className="text-[#5f3db1]">{searchInput}</span> active
                                    on the Wripple platform. The following information is associated with this person:
                                </p>

                                <p>Name: <b>{existingMember.fullName()}</b></p>


                                <p>
                                    Location: <b>{existingMember.location?.city}, {existingMember.location?.state}</b>
                                </p>
                                <p>Roles on Wripple:</p>

                                <ul>
                                    {existingMember.disciplines?.length > 0 ? existingMember.disciplines.map((role, rI) => (
                                        <span className="text-[#707683] font-semibold" key={role.id}>
                                            {role.discipline.name}{rI < existingMember.disciplines.length - 1 &&
                                            <span>,&nbsp;</span>}
                                        </span>
                                    )) : <span>None</span>}
                                </ul>

                                <div className="flex flex-col gap-1">
                                    <p>Team Position Title: </p>
                                    <Input.TextArea
                                        autoFocus={true}
                                        value={title}
                                        size="small"
                                        showCount maxLength={100}
                                        className="text-xs"
                                        autoSize
                                        disabled={isUpdating}
                                        onChange={(e) => setTitle(e.target.value)}
                                    />
                                </div>

                                <p>
                                    Please confirm that you want to
                                    invite <span className="text-[#5f3db1] font-semibold">{searchInput}</span> to join
                                    the team.
                                </p>
                            </div>
                        )
                    ) : (
                        <div className="flex flex-col gap-3">
                            <p>
                                We found <span className="text-[#5f3db1] font-semibold">{searchInput}</span> but they
                                are not
                                currently active on the Wripple Platform. Please confirm that you want to send an email
                                to
                                invite <span className="text-[#5f3db1] font-semibold">{searchInput}</span> to join the
                                team.
                            </p>
                            <p>
                                We will notify them that they have been invited to the team.
                            </p>
                        </div>
                    ))}
                    {existingMember === false && (

                        <div className="flex flex-col gap-3">
                            <p>
                                We did not find <span className="text-[#5f3db1] font-semibold">{searchInput}</span> on the Wripple
                                Platform. Please confirm that you want to send an email to
                                invite <span className="text-[#5f3db1] font-semibold">{searchInput}</span> to join the team.
                            </p>
                            <p>
                                They will first need to apply to join Wripple and complete our talent vetting process.
                            </p>
                        </div>
                    )}

                    <div className="flex flex-row gap-3 justify-end mt-[50px]">

                        <button className="btn btn-outline-dark w-[100px]"
                                disabled={isUpdating}
                                onClick={toggleSearchModal}>
                            Cancel
                        </button>

                        {existingMember !== undefined && (
                            <button className="btn btn-primary" disabled={isUpdating}
                                    onClick={onConfirmInvite}>
                                Confirm Invite
                            </button>
                        )}
                    </div>
                </div>

            </Modal>
        </React.Fragment>
    );
}

export default InviteTeamMemberModal;